/* eslint-disable prettier/prettier */
/* eslint-disable no-unused-vars */
import React from "react";
import { createRoot } from "react-dom/client";
import ForceLogout from "../services/api/ForceLogout";

/* ----------------------------------------------------------
   GLOBALS
----------------------------------------------------------- */
let warningTimeout = null;
let logoutTimeout = null;
let popupRoot = null;
let popupContainer = null;
let autoCheckInterval = null;

let lastActivityTime = Date.now();
let timerActive = false;
let listenersAttached = false;

let globalOnLogout = null;
let globalOnExtend = null;

// Store event handler reference (IMPORTANT)
let activityHandler = null;

/* ----------------------------------------------------------
   UI STYLES
----------------------------------------------------------- */
const styles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    background: "rgba(0,0,0,0.4)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 99999,
  },
  popup: {
    background: "#fff",
    padding: "20px 30px",
    borderRadius: "10px",
    width: "400px",
    textAlign: "center",
  },
  countdown: {
    marginTop: "10px",
    fontWeight: "bold",
    color: "#c0392b",
  },
  buttonRow: {
    marginTop: "20px",
    display: "flex",
    gap: "10px",
  },
  yesBtn: {
    flex: 1,
    padding: "10px",
    borderRadius: "5px",
    background: "#27ae60",
    color: "#fff",
    border: "none",
    cursor: "pointer",
  },
  noBtn: {
    flex: 1,
    padding: "10px",
    borderRadius: "5px",
    background: "#e74c3c",
    color: "#fff",
    border: "none",
    cursor: "pointer",
  },
};

/* ----------------------------------------------------------
   HIDE POPUP
----------------------------------------------------------- */
const hidePopup = () => {
  if (popupRoot) {
    popupRoot.unmount();
    popupRoot = null;
  }

  if (popupContainer && popupContainer.parentNode) {
    popupContainer.parentNode.removeChild(popupContainer);
    popupContainer = null;
  }
};

/* ----------------------------------------------------------
   SHOW SESSION WARNING POPUP
----------------------------------------------------------- */
const showSessionPopup = () => {
  if (popupRoot) return;

  popupContainer = document.createElement("div");
  document.body.appendChild(popupContainer);
  popupRoot = createRoot(popupContainer);

  const countdownSeconds = 60;

  function Popup() {
    const [timeLeft, setTimeLeft] = React.useState(countdownSeconds);

    React.useEffect(() => {
      const timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timer);

            // If user active in last 5 seconds → do not logout
            if (Date.now() - lastActivityTime < 5000) {
              hidePopup();
              return 0;
            }

            globalOnLogout();
            hidePopup();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }, []);

    return (
      <div style={styles.overlay}>
        <div style={styles.popup}>
          <h3>Your session is about to expire.</h3>
          <p>Would you like to continue?</p>

          <p style={styles.countdown}>Auto logout in {timeLeft}s</p>

          <div style={styles.buttonRow}>
            <button
              type="button"
              style={styles.noBtn}
              onClick={() => {
                globalOnLogout();
                hidePopup();
              }}
            >
              Logout
            </button>

            <button
              type="button"
              style={styles.yesBtn}
              onClick={() => {
                hidePopup();
                globalOnExtend();
              }}
            >
              Extend Session
            </button>
          </div>
        </div>
      </div>
    );
  }

  popupRoot.render(<Popup />);
};

/* ----------------------------------------------------------
   RESET TIMERS
----------------------------------------------------------- */
const resetTimer = () => {
  clearTimeout(warningTimeout);
  clearTimeout(logoutTimeout);

  warningTimeout = setTimeout(() => {
    // console.log("⚠ Warning popup triggered (29 min)");
    showSessionPopup();
  }, 29 * 60 * 1000);

  logoutTimeout = setTimeout(() => {
    // console.log("⛔ Auto logout triggered (30 min)");
    globalOnLogout();
    hidePopup();
  }, 30 * 60 * 1000);
};

/* ----------------------------------------------------------
   USER ACTIVITY HANDLING
----------------------------------------------------------- */
const ACTIVITY_EVENTS = [
  "click",
  "mousemove",
  "keydown",
  "scroll",
  "touchstart",
];

const attachActivityListeners = () => {
  if (listenersAttached) return;

  activityHandler = () => {
    // console.log("🟢 User activity detected");   // NOW PRINTS!
    lastActivityTime = Date.now();
    resetTimer();
  };

  ACTIVITY_EVENTS.forEach((ev) =>
    document.addEventListener(ev, activityHandler)
  );

  listenersAttached = true;
  // console.log("✔ Activity listeners attached");
};

const removeActivityListeners = () => {
  if (!listenersAttached) return;

  ACTIVITY_EVENTS.forEach((ev) =>
    document.removeEventListener(ev, activityHandler)
  );

  listenersAttached = false;
  // console.log("❌ Activity listeners removed");
};

/* ----------------------------------------------------------
   PUBLIC API
----------------------------------------------------------- */
export const startSessionTimer = (onLogout, onExtend) => {
  if (timerActive) return;

  globalOnLogout = onLogout;
  globalOnExtend = onExtend;

  timerActive = true;
  lastActivityTime = Date.now();

  resetTimer();
  attachActivityListeners();

  // console.log("▶ Session timer started");
};

export const clearSessionTimer = () => {
  timerActive = false;

  clearTimeout(warningTimeout);
  clearTimeout(logoutTimeout);

  hidePopup();
  removeActivityListeners();

  // console.log("⏹ Session timer cleared");
};

/* ----------------------------------------------------------
   AUTO CHECK (browser reopen case)
----------------------------------------------------------- */
export const startAutoExpiryCheck = (onLogout) => {
  if (autoCheckInterval) clearInterval(autoCheckInterval);

  autoCheckInterval = setInterval(async () => {
    const loginStamp = localStorage.getItem("LoginTime");
    if (!loginStamp) return;

    const [y, m, d, hh, mm, ss] = loginStamp.split("-").map(Number);
    const loginDate = new Date(y, m - 1, d, hh, mm, ss);
    const now = new Date();

    const diffMinutes = (now - loginDate) / 60000;

    if (diffMinutes >= 120) {
      const loginId = localStorage.getItem("loginId");

      if (loginId) {
        try {
          await ForceLogout.userLogout(loginId);
        } catch (e) {
          console.log("Logout API error:", e);
          clearSessionTimer();
          localStorage.clear();
          sessionStorage.clear();
          onLogout();
        }
      }
      clearSessionTimer();
      localStorage.clear();
      sessionStorage.clear();
      onLogout();
    }
  }, 15000);

  // console.log("⏳ Auto-expiry checker active");
};

export const stopAutoExpiryCheck = () => {
  clearInterval(autoCheckInterval);
  autoCheckInterval = null;
  // console.log("🛑 Auto-expiry checker stopped");
};
